import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Grid, Header, Segment, Card, Icon, Statistic, Image, Button, Divider } from 'semantic-ui-react';
import { AuthStdFooter } from "../../utils/auth-std-footer";
import { getMenuIdForFeature } from '../../utils/roleUtils';
import { fetchStatistics, selectStatisticsByView, selectStatisticsLoading } from '../../slices/statisticsSlice';
import { STAT_SETS } from '../../constants/statisticsFlags';
import '../../styles/landing-pages.css';
import '../../styles/article.css';
import bookStack from '../../assets/justBookStack.svg'
import hourGlass from '../../assets/hourGlassLine.svg'
import checkBox from '../../assets/checkListLine.svg'
import voiceActressBW from '../../assets/micAndshield.svg'
// import lightBulbSqr from '../../assets/lightBulbSqr.png'
import myProfileLine from '../../assets/myProfileLine.svg'
import lightening from '../../assets/lightening.svg'
import bookDetails from '../../assets/bookDetails.svg'
import myLibrary from '../../assets/myLibrary.svg'
// import importStory from '../../assets/importStory.svg'
import profileIcon from '../../assets/profileLineGlasses.svg'
import micContract from '../../assets/mic-and-contract.svg'



const BookCreatorLandingPage = () => {
  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.user.profile);
  const userName = userProfile?.data?.title || 'Book Creator';

  // Fetch statistics from Redux
  const stats = useSelector(selectStatisticsByView('bookCreator_portfolio'));
  const isLoadingStats = useSelector(selectStatisticsLoading('bookCreator_portfolio'));

  // Fetch statistics on component mount
  useEffect(() => {
    dispatch(fetchStatistics({
      viewType: 'bookCreator_portfolio',
      flags: STAT_SETS.BOOK_CREATOR_PORTFOLIO
    }));
  }, [dispatch]);

  // Quick action cards for book creators
  const quickActions = [
    {
      icon: 'plus',
      img: myLibrary,
      title: 'Add Book',
      description: 'Add Litgroups/Books',
      // 2026-04-21: route through the Dashboard shell so the BC sidebar is
      // visible while they build Literary Groups. TEXT-LIBRARY menuId renders
      // TextLibraryLandingPage (the Literary Group Builder).
      // was link: '/landing/text-library'  →  was link: '/add-litgroups-books'
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('textLibrary') }
    },
    {
      icon: 'book',
      img: bookDetails,
      title: 'Book Worksheet',
      description: 'View and manage your books',
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('bookDetails') }
    },
    {
      icon: 'file text',
      img: myLibrary,
      title: 'Text Library',
      description: 'Access your text library',
      link: '/add-books-landing'

      // link: '/landing/text-library'
      // link: '/dashboard'
    },
    // {
    //   icon: 'upload',
    //   img: importStory,
    //   title: 'Import Story',
    //   description: 'Upload a new book or story',
    //   link: '/dashboard',
    //   state: { menuSelection: getMenuIdForFeature('storyImporter') }
    // },
    {
      icon: 'users',
      img: micContract,
      title: 'Manage Voiced Opportunities',
      description: 'Manage voice actor assignments',
      link: '/opportunities/manage'
    },

    {
      icon: 'users',
      img: myLibrary,
      title: 'Opportunities',
      description: `Manage Opportunity Portfolio for ${userName} `,
      link: '/manage-opportunities-landing'
    },
    {
      icon: 'rocket',
      img: bookStack,
      title: 'Publish Literary Groups',
      description: 'Create LitGroups from your prototyped library',
      link: '/publish-prototypes'
      // was: link: '/landing/text-library'
    },
    {
      icon: 'cogs',
      img: micContract,
      title: 'Voice Cloning Pipeline',
      description: 'Track approved recordings through ElevenLabs cloning',
      link: '/voice-cloning/pipeline'
    },
    // Phase 4d Step 3 — Rendering Studio R1. The natural next click after
    // a clone flips to `success` on the pipeline page: jump here to
    // eye-test the cloned voice across the character's emotion grid.
    {
      icon: 'play circle',
      img: micContract,
      title: 'Rendering Studio',
      description: 'Eye-test cloned voices on the per-character emotion grid',
      link: '/dashboard/book-creator/rendering-studio'
    },
    {
      icon: 'users',
      img: micContract,
      title: 'AAI Talent Agency',
      description: 'Casting Studio — tier your cast and see the voice-style roster',
      link: '/dashboard/book-creator/talent-agency'
    },
    // Recipient layer, stage 6 (read-only). Who has redeemed a key for one of this creator's
    // books, what they hold and what they took. Deliberately NOT the admin Client Downloads
    // page: that one is adminAuth and quirkmaker is the only admin, so a book creator could
    // never see their own readers there.
    {
      icon: 'users',
      img: bookStack,
      title: 'Releases Manager',
      description: 'See the readers holding your released books',
      link: '/dashboard/book-creator/releases'
    },
  ];


  // Book creator statistics - now using real data from Redux
  const statistics = [
    {
      label: 'My Books',
      value: isLoadingStats ? '...' : (stats?.bookCount || '0'),
      icon: 'book',
      img: bookStack,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('bookDetails') }
    },
    {
      label: 'In Progress',
      value: isLoadingStats ? '...' : (stats?.inProgressCount || '0'),
      icon: 'hourglass half',
      img: hourGlass,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('bookDetails') }
    },
    {
      label: 'Completed',
      value: isLoadingStats ? '...' : (stats?.completedCount || '0'),
      icon: 'check circle',
      img: checkBox,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('bookDetails') }
    },
    {
      label: 'Voice Actors',
      value: isLoadingStats ? '...' : (stats?.voiceActorCount || '0'),
      icon: 'microphone',
      img: voiceActressBW,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('voiceActorAssignments') }
    },
    {
      label: 'My Profile',
      value: '',
      icon: 'user',
      img: profileIcon,
      link: '/profile'
    }
  ];

  // Recent books
  const recentBooks = [
    { title: 'The Shadow\'s Embrace', status: 'In Progress', completion: 65 },
    { title: 'Echoes of Tomorrow', status: 'Completed', completion: 100 },
    { title: 'Whispers in the Dark', status: 'Not Started', completion: 0 }
  ];

  return (
    <Container fluid className="landing-page book-creator-landing">
      <Container style={{ marginTop: "30px" }}>
        <Grid className="articleListWidth">
          <Grid.Row>
            <Grid.Column width={3}>
              <div className="img-with-gradient">
                <Image src="/static/media/shutterstock_2294396055-1024x1024.2d3b550af78ebc4d2e7b.jpg" />
              </div>
            </Grid.Column>
            <Grid.Column width={13} textAlign="center">
              <span className="blgListItemTitle">Welcome, {userName}</span>
              <span className="blgListSubTitle">Book Creator Dashboard</span>
              <p className="landing-subheading-text">
                As a Book Creator, you can upload books, manage your text library, and assign voice actors to bring your stories to life.
                Use the quick actions below to navigate to common tasks.
              </p>
              <div style={{ textAlign: 'left', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/dashboard"
                  size="large"
                  className="landing-goto-button"
                >
                  Go to Main Dashboard
                </Button>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Grid className="articleListWidth" style={{ marginTop: "20px", borderTop: "solid 1px #AAAAAA" }}></Grid>
      </Container>

      <Container style={{ marginTop: '2em', marginBottom: '2em' }}>
        <Grid>
          <Grid.Row >
            <Grid.Column width={6}>
              <Header as="h2">
                {/* <Icon name="dashboard" /> */}
                <img src={myProfileLine} alt="" />
                <Header.Content style={{
                  fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                  fontWeight: '400',
                  fontSize: '2.5rem'
                }}>
                  Your Portfolio
                  <Header.Subheader>Overview of your books and projects</Header.Subheader>
                </Header.Content>
              </Header>
            </Grid.Column>
            <Grid.Column width={10}>
              <div >
                <Statistic.Group widths="five">
                  {statistics.map((stat, index) => {
                    const StatisticContent = (
                      <>
                        <Statistic.Value>
                          <div style={{
                            fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                            fontWeight: '300',
                            fontSize: '3.0rem'
                          }}><img src={stat.img} alt="" /> {stat.value}</div>
                        </Statistic.Value>
                        <Statistic.Label style={{
                          fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                          fontWeight: '300',
                          fontSize: '1.1rem'
                        }}>{stat.label}</Statistic.Label>
                      </>
                    );

                    // If link is provided, wrap with Link component
                    if (stat.link) {
                      return (
                        <Statistic
                          key={index}
                          as={Link}
                          to={stat.link}
                          state={stat.state}
                          className="statistic-link"
                        >
                          {StatisticContent}
                        </Statistic>
                      );
                    }

                    // Otherwise, render as a regular Statistic
                    return (
                      <Statistic key={index}>
                        {StatisticContent}
                      </Statistic>
                    );
                  })}
                </Statistic.Group>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Divider />

        <Header as="h2" dividing style={{ marginTop: '2em' }}>
          {/* <Icon name="bolt" /> */}
          <img src={lightening} alt="" />
          <Header.Content>
            Quick Actions
            <Header.Subheader>Common book creator tasks</Header.Subheader>
          </Header.Content>
        </Header>

        <Card.Group itemsPerRow={5} stackable>
          {quickActions.map((action, index) => (
            <Card
              key={index}
              as={Link}
              to={action.link}
              state={action.state}
              raised
            >
              <Card.Content>
                <img src={action.img} alt="" style={{ height: '80px', display: 'block', margin: '0 auto 1em', color: '#2c5d8a' }} />
                {/* <Icon
                  name={action.icon}
                  size="huge"
                  style={{ display: 'block', margin: '0 auto 1em', color: '#2c5d8a' }}
                /> */}
                <Card.Header textAlign="center">{action.title}</Card.Header>
                <Card.Description textAlign="center">
                  {action.description}
                </Card.Description>
              </Card.Content>
            </Card>
          ))}
        </Card.Group>

        <Grid columns={2} stackable style={{ marginTop: '2em' }}>
          <Grid.Column>
            <Header as="h2" dividing>
              {/* <Icon name="book" /> */}
              <img src={bookStack} alt="" style={{ height: '50px' }} />
              <Header.Content>
                Recent Books
                <Header.Subheader>Your latest projects</Header.Subheader>
              </Header.Content>
            </Header>

            <Segment>
              {recentBooks.map((book, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <Divider />}
                  <Grid>
                    <Grid.Column width={10}>
                      <Header as="h4">{book.name}</Header>
                      <p>Status: {book.status}</p>
                    </Grid.Column>
                    <Grid.Column width={6} textAlign="right">
                      <Statistic size="tiny">
                        <Statistic.Value>{book.completion}%</Statistic.Value>
                        <Statistic.Label>Complete</Statistic.Label>
                      </Statistic>
                    </Grid.Column>
                  </Grid>
                </React.Fragment>
              ))}
              <div style={{ textAlign: 'center', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/dashboard"
                  state={{ menuSelection: getMenuIdForFeature('bookDetails') }}
                  compact
                >
                  View All Books
                </Button>
              </div>
            </Segment>
          </Grid.Column>

          <Grid.Column>
            <Header as="h2" dividing>
              <img src={voiceActressBW} alt="" style={{ height: '50px' }} />
              <Header.Content>
                Voice Actor Assignments
                <Header.Subheader>Current voice actor projects</Header.Subheader>
              </Header.Content>
            </Header>

            <Segment>
              <p>You have 3 pending voice actor assignments</p>
              <p>2 voice actors have submitted new recordings</p>
              <p>1 book is ready for final review</p>
              <div style={{ textAlign: 'center', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/dashboard"
                  state={{ menuSelection: getMenuIdForFeature('voiceActorAssignments') }}
                  compact
                >
                  Manage Assignments
                </Button>
              </div>
            </Segment>

            <Segment>
              <Header as="h4">Voice Actor Mode</Header>
              <p>You can also record your own voice for your books</p>
              <Button
                as={Link}
                to="/dashboard"
                state={{ menuSelection: getMenuIdForFeature('voiceLab') }}
                fluid
                style={{ backgroundColor: '#2c5d8a', color: 'white' }}
              >
                <Icon name="microphone" /> Switch to Voice Actor Mode
              </Button>
            </Segment>
          </Grid.Column>
        </Grid>
      </Container>

      {AuthStdFooter()}
    </Container>
  );
};

export default BookCreatorLandingPage;
